import {
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {Router} from '@angular/router';
import {Utils} from '../../../shared/utils/utils';
import {Observable, Subscription} from 'rxjs';
import {Environment, SharedService} from '../../services/store/shared-store/shared-storage.service';
import {MatDialog} from '@angular/material/dialog';
import {SubmissionService} from '../../services/management/submission/submission.service';
import {FlowManagerService} from '../../../modules/flow/services/flow-manager.service';
import {FlowManagementComponent} from '../flow-management/flow-management.component';
import {FlowWebSocketsService} from '../../../modules/flow/services/flowBuilder/flow-web-sockets.service';
import {FlowBuilderService} from '../../../modules/flow/services/flowBuilder/flow-builder.service';
import {ZoomService} from '../../../modules/flow/services/flowBuilder/zoom.service';
import {RolesService} from '../../services/roles/roles.service';
import {FileClickEmitter} from '../../../shared/components/clickatell-tree/clickatell-tree.interface';
import {MatMenuTrigger} from '@angular/material/menu';
import {Store} from '@ngrx/store';
import {selectFlowModeState} from '../../state/selectors/flow.selector';
import {FlowModeState, FlowType} from "../../../shared/models/state/Flow";
import {flowMetaDataAction, flowModeAction} from "../../state";
import {FlowMetaData} from "../../../shared/models";
import {tap} from "rxjs/operators";

@Component({
  selector: 'app-left-sidebar',
  templateUrl: './left-sidebar.component.html',
  styleUrls: ['./left-sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LeftSidebarComponent implements OnInit, DoCheck, OnDestroy, AfterViewInit {
  @Output() loadFlow: any = new EventEmitter();
  @Output() newFlow: any = new EventEmitter();
  @Output() loadRoute: any = new EventEmitter();
  @Input() isTrashOpen: string;

  selectedForDuplication: string;
  multi = true;
  activeEnv: string;
  openAll = true;
  panelOpenState = false;
  flowDataSource: Observable<any[]>;
  canEditFlow: boolean;
  searchForValue: string;
  globalEnvironment: Environment;
  currentEnvironments: string;
  clientId: string;
  isUserInternal: boolean;
  isClientAdmin: boolean;

  @ViewChild(MatMenuTrigger)
  contextMenu: MatMenuTrigger;
  contextMenuPosition = { x: '0px', y: '0px' };
  private hasNotifications: boolean = false;
  private myCountSubscription: Subscription;

  public flowState$: Observable<String>;

  @ViewChild('parent') leftSideBar: ElementRef;

  constructor(
    public router: Router,
    public flowManagerService: FlowManagerService,
    public rolesService: RolesService,
    public sharedService: SharedService,
    public zoomService: ZoomService,
    private utils: Utils,
    public dialog: MatDialog,
    public flowBuilderService: FlowBuilderService,
    public flowWebSocket: FlowWebSocketsService,
    private submissionService: SubmissionService,
    private store: Store
  ) {}

  ngOnInit() {
    this.isUserInternal = this.rolesService.isUserInternal();
    this.isClientAdmin = this.rolesService.isClientAdmin();
    this.fetchFlows(true);
    this.newFlowClick(null, true);
    this.globalEnvironment = Object.assign({}, this.sharedService.getGlobalEnvironment());
    this.notifications();
    this.sharedService.setHasNotifications = true;
  }

  ngAfterViewInit(): void {
    this.flowState$ = this.store.select(selectFlowModeState);
  }

  ngOnDestroy(): void {
    this.myCountSubscription?.unsubscribe();
  }

  ngDoCheck(): void {
    if (
      JSON.stringify(this.globalEnvironment) !==
      JSON.stringify(this.sharedService.getGlobalEnvironment())
    ) {
      this.fetchFlows(true);
      this.globalEnvironment = Object.assign({}, this.sharedService.getGlobalEnvironment());
    }

    if (this.sharedService.isNewFlowSaved() === true) {
      this.sharedService.setNewFlowSaved(false);
      this.fetchFlows(true);
    }
  }

  route(path: string) {
    // NB! Please leave this code block in for possible future usage
    // if (!this.sharedService.getIsFlowChanged()) {
    //   this.sharedService.isNodeSaved(false);
    //   this.setNewFlow();
    // }
    this.loadRoute.emit(path);
  }

  fetchFlows(reload: boolean) {
    this.flowDataSource = this.flowManagerService.getFlows(reload);
    this.flowDataSource.subscribe((flows) => {
      if (flows) {
        this.sharedService.clearFlows();
        for (const flow of flows) {
          // @ts-ignore
          if (flow.children) {
            // @ts-ignore
            for (const child of flow.children) {
              this.sharedService.addFlows(child);
            }
          } else {
            this.sharedService.addFlows(flow);
          }
        }
      }
    });
  }

  userCanEditFlow(): boolean {
    return this.rolesService.canEditFlows();
  }

  flowClicked(click: FileClickEmitter) {
    this.flowBuilderService.setCurrFlow(click);
    this.loadFlow.emit(click);
    this.setFlowMode(click.additionalData.flowType);
  }

  flowManagement(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dialog.open(FlowManagementComponent, {
      panelClass: 'flowManagementDialog',
      width: '80%',
      height: '65%',
    });
  }

  setNewFlow(flowType: FlowType = FlowType.ChatFlow) {
    const emit: FileClickEmitter = {
      name: this.sharedService.getNewFlowName(),
      path: this.sharedService.getNewFlowName(),
    };

    const flowMetaData: FlowMetaData = {
      flowId: '',
      flowName: '',
      directory: '',
      flowStatus: '',
      flowType: flowType
    };

    this.store.dispatch(flowMetaDataAction({ flowMetaData }));
    this.setFlowMode(flowType);

    this.loadFlow.emit(emit);
    this.sharedService.setIsFlowChanged(false);
  }

  private setFlowMode(flowType: FlowType = FlowType.ChatFlow) {
    switch (flowType) {
      case FlowType.ApiFlow:
        this.store.dispatch(flowModeAction({flowModeState: FlowModeState.API_FLOW_MODE}));
        break;
      case FlowType.ChatFlow:
      default:
        this.store.dispatch(flowModeAction({flowModeState: FlowModeState.FLOW_MODE}));
        break;
    }
  }

  newFlowClick(flowType: FlowType, validateUrl = false) {
    const flowPathName = this.sharedService.getFlowPathName();

    if (validateUrl && this.validateUrl() && this.rolesService.canEditFlows()) {
      return;
    }

    this.sharedService.setFlowName = this.sharedService.getNewFlowName();
    const currentURL = this.router.url;

    if (!currentURL.includes('__NEW_FLOW__') && !this.sharedService.getIsFlowChanged()) {
      this.setNewFlow(flowType);
    }

    if (currentURL.includes('__NEW_FLOW__') && !this.sharedService.isFlowPristine && !this.sharedService.getIsFlowChanged()) {
      this.sharedService.isNodeSaved(false);
    } else if (this.sharedService.getIsFlowChanged()) {
      this.sharedService.isNodeSaved(true);
      this.sharedService.setFlowPathName(flowPathName);
    }
  }

  searchForChange(event: any) {
    this.searchForValue = event.value;
  }

  notifications(): void {
    if (!this.rolesService.canDeploy() || !this.rolesService.isReviewer()) {
      this.hasNotifications = false;
      return;
    }

    if (this.hasNotifications) {
      return;
    }

    this.myCountSubscription = this.submissionService
      .getSubmissionsCount()
      .subscribe((SubmissionsCount) => {
        if (this.rolesService.isReviewer() && this.rolesService.canDeploy()) {
          this.hasNotifications =
            SubmissionsCount['my_approvals_count'] > 0 ||
            SubmissionsCount['production_ready_count'] > 0;
          return;
        }

        if (this.rolesService.isReviewer()) {
          this.hasNotifications = SubmissionsCount['my_approvals_count'] > 0;
          return;
        }

        if (this.rolesService.canDeploy()) {
          this.hasNotifications = SubmissionsCount['production_ready_count'] > 0;
          return;
        }
      });
  }

  hasNotification(): boolean {
    return this.hasNotifications && this.sharedService.getHasNotifications;
  }

  validateUrl(): boolean {
    const url = window.location.pathname;
    const urlList = url.split('/');
    const entityId = this.sharedService.getEntityId();
    const entityType = this.sharedService.getEntityType();

    return (
      urlList.length === 7 &&
      url.includes('/flow/') &&
      entityId === urlList[5] &&
      entityType === urlList[4]
    );
  }

  protected readonly FlowType = FlowType;

}
